import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Center, CloseButton, Dialog, Heading, HStack, Portal, Spinner, StackProps, Text, VStack } from '@chakra-ui/react';
import { ChatThread } from '@src/components/sco/Chat/ChatThread';
import { DEFAULT_INTAKE_PLACEHOLDERS, SignupRequestChatIntakeAskBar, SignupRequestChatIntakeAskMeta } from '@src/components/sco/SignupRequestChatIntake/SignupRequestChatIntakeAskBar';
import { INTAKE_DEFAULT_TOOL_SLUG } from '@src/components/sco/SignupRequestChatIntake/constants';
import { StartupscomLogo } from '@src/components/sco/StartupscomLogo.tsx';
import { ChevronRightIcon } from '@src/components/sco/icons.tsx';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/router';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useCallback, useEffect, useRef, useState } from 'react';

/** Extract GraphQL / Apollo error messages (Apollo Client 3 & 4 shapes). */
function apolloErrorMessage(err: unknown): string {
  if (!err) {
    return 'Something went wrong. Please try again.';
  }
  if (typeof err === 'object' && err !== null) {
    const e = err as {
      message?: string;
      graphQLErrors?: Array<{
        message?: string;
      }>;
      errors?: Array<{
        message?: string;
      }>;
      cause?: unknown;
    };
    const fromGql = e.graphQLErrors?.[0]?.message || e.errors?.[0]?.message;
    if (fromGql) {
      return fromGql;
    }
    if (e.message && e.message !== 'Error') {
      return e.message;
    }
    if (e.cause) {
      return apolloErrorMessage(e.cause);
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return 'Something went wrong. Please try again.';
}
export type SignupRequestChatIntakeProps = {
  /** Override tool slug (default advisor-wil). */
  toolSlug?: string;
  funnelIdentifier?: string;
  /** Static placeholder override (disables rotation when set). */
  placeholder?: string;
  /** Rotating placeholders for the ask bar (default founder prompts). */
  placeholders?: string[];
  /** Hide the ask-bar attach-file control. */
  hideFileAttach?: boolean;
  containerProps?: StackProps;
};

/**
 * Guest-capable Wil / tool chat intake:
 * 1. Big ask bar (rotating placeholder; isolated input component)
 * 2. On submit: upsertSignupRequest → upsertToolSession (SignupRequest attach) → first comment
 * 3. Open chat + CTA in a modal (desktop container.xl; mobile full-screen)
 */
export const SignupRequestChatIntake: React.FC<SignupRequestChatIntakeProps> = ({
  toolSlug = INTAKE_DEFAULT_TOOL_SLUG,
  funnelIdentifier = 'signup-request-chat-intake',
  placeholder,
  placeholders = DEFAULT_INTAKE_PLACEHOLDERS,
  hideFileAttach = false,
  containerProps
}) => {
  const router = useRouter();
  const {
    currentUser,
    refetchCurrentUser
  } = useCurrentUser();
  const [signupRequestId, setSignupRequestId] = useQueryState('signupRequestId', parseAsString);
  const [toolSessionId, setToolSessionId] = useQueryState('toolSessionId', parseAsString);
  const [prompt, setPrompt] = useQueryState('prompt', parseAsString);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  /** User clicked Get Started / Continue — only then auto-commit after auth. */
  const [pendingCommit, setPendingCommit] = useState(false);
  /** Bumped on retry so the commit effect re-runs after a failure. */
  const [commitRetryKey, setCommitRetryKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /** Chat + CTA live in a modal (not on-page expand). */
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  /** User closed the chat modal; do not auto-reopen until they ask again. */
  const chatDismissedRef = useRef(false);
  const showSignupCta = !currentUser || currentUser.isGuest;
  const commitAttemptedForUserRef = useRef<string | null>(null);
  const toolQuery = useQuery(gql`
      query SignupRequestChatIntakeTool($slug: String!) {
        scoTool(slug: $slug) {
          id
          toolId
          slug
          title
          allowAnonymousSessions
        }
      }
    `, {
    variables: {
      slug: toolSlug
    }
  });
  const sessionQuery = useQuery(gql`
      query SignupRequestChatIntakeSession($toolSessionId: String!) {
        scoToolSession(toolSessionId: $toolSessionId) {
          id
          toolSessionId
          threadId
          userId
          attachmentType
          attachmentId
        }
      }
    `, {
    variables: {
      toolSessionId
    },
    skip: !toolSessionId
  });
  const [upsertSignupRequest] = useMutation(gql`
    mutation SignupRequestChatIntakeUpsertSignupRequest($input: UpsertSignupRequestInput!) {
      upsertSignupRequest(input: $input) {
        signupRequest {
          id
          signupRequestId
        }
      }
    }
  `);
  const [upsertToolSession] = useMutation(gql`
    mutation SignupRequestChatIntakeUpsertToolSession($input: UpsertToolSessionInput!) {
      upsertToolSession(input: $input) {
        toolSession {
          id
          toolSessionId
          threadId
        }
      }
    }
  `);
  const [upsertChatComment] = useMutation(gql`
    mutation SignupRequestChatIntakeUpsertChatComment($input: UpsertChatCommentInput!) {
      upsertChatComment(input: $input) {
        comment {
          id
          commentId
        }
        thread {
          id
          threadId
        }
      }
    }
  `);
  const [commitSignupRequest] = useMutation(gql`
    mutation SignupRequestChatIntakeCommit($input: UpsertSignupRequestInput!) {
      upsertSignupRequest(input: $input) {
        signupRequest {
          id
          signupRequestId
          groupRequestId
          groupRequestTypeKey
          title
          groupRequest {
            id
            groupRequestId
          }
        }
        groupRequest {
          id
          groupRequestId
        }
      }
    }
  `);
  const threadId = sessionQuery.data?.scoToolSession?.threadId;
  const sessionQueryDenied = !!toolSessionId && !!sessionQuery.error && /USER_NOT_AUTHORIZED|NOT_FOUND/i.test(String(sessionQuery.error.message || ''));
  const expanded = !!(toolSessionId && threadId && !sessionQueryDenied);
  const handleAsk = useCallback(async (query: string, meta?: SignupRequestChatIntakeAskMeta) => {
    const text = query.trim();
    if (!text || isBootstrapping) {
      return;
    }
    const storedFileIds = (meta?.storedFileIds || []).filter(Boolean);
    setError(null);
    chatDismissedRef.current = false;
    setIsChatModalOpen(true);
    setIsBootstrapping(true);
    try {
      const tool = toolQuery.data?.scoTool;
      if (!tool?.toolId) {
        throw new Error('Tool not found. Try again in a moment.');
      }
      if (tool.allowAnonymousSessions === false) {
        throw new Error('This tool is not open for guests yet (allowAnonymousSessions is off).');
      }
      let nextSignupRequestId = signupRequestId;
      if (!nextSignupRequestId) {
        const srRes = await upsertSignupRequest({
          variables: {
            input: {
              funnelIdentifier,
              title: text.slice(0, 200)
            }
          }
        });
        nextSignupRequestId = srRes.data?.upsertSignupRequest?.signupRequest?.signupRequestId;
        if (!nextSignupRequestId) {
          throw new Error(apolloErrorMessage(srRes.error) || 'Could not create signup request');
        }
        await setSignupRequestId(nextSignupRequestId);
      }

      // Drop stale URL session ids that the server rejects for guests
      let nextToolSessionId = toolSessionId && !sessionQueryDenied ? toolSessionId : null;
      let nextThreadId = nextToolSessionId && threadId && !sessionQueryDenied ? threadId : null;
      if (!nextToolSessionId) {
        if (toolSessionId && sessionQueryDenied) {
          await setToolSessionId(null);
        }
        const tsRes = await upsertToolSession({
          variables: {
            input: {
              toolId: tool.toolId,
              attachmentType: 'SignupRequest',
              attachmentId: nextSignupRequestId,
              title: text.slice(0, 120),
              inputAsJson: {
                initialPrompt: text
              }
            }
          }
        });
        if (tsRes.error) {
          throw new Error(apolloErrorMessage(tsRes.error));
        }
        const session = tsRes.data?.upsertToolSession?.toolSession;
        nextToolSessionId = session?.toolSessionId;
        nextThreadId = session?.threadId;
        if (!nextToolSessionId) {
          throw new Error('Could not create tool session');
        }
        await setToolSessionId(nextToolSessionId);
      }

      // Thread may be created in afterSave — refetch if missing
      if (!nextThreadId && nextToolSessionId) {
        try {
          const refetch = await sessionQuery.refetch({
            toolSessionId: nextToolSessionId
          });
          nextThreadId = refetch.data?.scoToolSession?.threadId;
        } catch (refetchErr) {
          // If we cannot read the session as guest, force a clean create next time
          await setToolSessionId(null);
          throw new Error(apolloErrorMessage(refetchErr));
        }
      }
      if (!nextThreadId) {
        await new Promise(r => setTimeout(r, 500));
        try {
          const refetch_0 = await sessionQuery.refetch({
            toolSessionId: nextToolSessionId
          });
          nextThreadId = refetch_0.data?.scoToolSession?.threadId;
        } catch (refetchErr_0) {
          await setToolSessionId(null);
          throw new Error(apolloErrorMessage(refetchErr_0));
        }
      }
      if (!nextThreadId) {
        throw new Error('Chat thread is not ready yet. Please try again.');
      }
      const commentRes = await upsertChatComment({
        variables: {
          input: {
            threadId: nextThreadId,
            body: text,
            ...(storedFileIds.length > 0 ? {
              storedFileIds
            } : {})
          }
        }
      });
      if (commentRes.error) {
        throw new Error(apolloErrorMessage(commentRes.error));
      }
      await sessionQuery.refetch({
        toolSessionId: nextToolSessionId
      });
    } catch (err: unknown) {
      console.error(err);
      setError(apolloErrorMessage(err));
    } finally {
      setIsBootstrapping(false);
    }
  }, [funnelIdentifier, isBootstrapping, sessionQuery, sessionQueryDenied, setSignupRequestId, setToolSessionId, signupRequestId, threadId, toolQuery.data?.scoTool, toolSessionId, upsertChatComment, upsertSignupRequest, upsertToolSession]);

  /**
   * After user clicks Get Started and register modal succeeds (or was already
   * authenticated), enrich from tool-chat when needed + commitToGroupRequest, then
   * route into the manage workspace. Gated on `pendingCommit` so casual chat
   * while logged-in does not auto-commit.
   *
   * IMPORTANT: do not treat React Strict Mode / effect cleanup as a reason to
   * skip navigation or leave isCommitting stuck true. The previous pattern set
   * `cancelled` on cleanup, then skipped both router.push and setIsCommitting(false)
   * — modal hung forever even though the workspace was created.
   */
  useEffect(() => {
    const userId = currentUser?.userId;
    if (!pendingCommit) {
      return;
    }
    if (!currentUser?.isRegistered || !userId || !signupRequestId) {
      return;
    }
    // Wait until register modal is closed
    if (prompt === 'register' || prompt === 'login') {
      return;
    }
    const attemptKey = `${userId}:${signupRequestId}:${commitRetryKey}`;
    if (commitAttemptedForUserRef.current === attemptKey) {
      return;
    }
    commitAttemptedForUserRef.current = attemptKey;
    let ignoreStateUpdates = false;
    (async () => {
      setIsCommitting(true);
      setError(null);
      try {
        try {
          await refetchCurrentUser?.();
        } catch {
          // Non-fatal — cookie session may already be valid for the mutation
        }
        const res = await commitSignupRequest({
          variables: {
            input: {
              signupRequestId,
              commitToGroupRequest: true,
              analyzeToolChatAndCommit: true
            }
          }
        });
        if (res.error) {
          throw new Error(apolloErrorMessage(res.error));
        }
        const payload = res.data?.upsertSignupRequest;
        const groupRequestId = payload?.groupRequest?.groupRequestId || payload?.signupRequest?.groupRequest?.groupRequestId || payload?.signupRequest?.groupRequestId;
        if (!groupRequestId) {
          throw new Error('Commit succeeded but no workspace was created.');
        }

        // Always navigate on success — even if this effect instance was cleaned up
        // (Strict Mode remount). Hard navigation avoids soft-router races after auth.
        if (!ignoreStateUpdates) {
          setPendingCommit(false);
        }
        const dest = `/requests/manage/${groupRequestId}/tasks`;
        try {
          await router.push(dest);
        } catch {
          window.location.assign(dest);
        }
        // Fallback if soft nav does not unload (e.g. same-layout edge cases)
        window.setTimeout(() => {
          if (typeof window !== 'undefined' && !window.location.pathname.includes(groupRequestId)) {
            window.location.assign(dest);
          }
        }, 750);
      } catch (err_0) {
        console.error(err_0);
        if (!ignoreStateUpdates) {
          setError(apolloErrorMessage(err_0));
          // Allow retry for the same key only via commitRetryKey bump
          commitAttemptedForUserRef.current = null;
        }
      } finally {
        // Always clear committing so the modal cannot hang if cleanup raced the request
        setIsCommitting(false);
      }
    })();
    return () => {
      // Only suppress stale setState from a superseded effect; never block navigation
      // after a successful commit (that caused the hang).
      ignoreStateUpdates = true;
    };
  }, [commitRetryKey, commitSignupRequest, currentUser?.isRegistered, currentUser?.userId, pendingCommit, prompt, refetchCurrentUser, router, signupRequestId]);

  // Deep-link / resume: open modal when a session is ready unless the user dismissed it.
  useEffect(() => {
    if (expanded && !chatDismissedRef.current) {
      setIsChatModalOpen(true);
    }
  }, [expanded]);

  /** Close chat modal and drop session ids from the URL so the next ask starts clean. */
  const closeChatModal = useCallback(() => {
    chatDismissedRef.current = true;
    setIsChatModalOpen(false);
    void setSignupRequestId(null);
    void setToolSessionId(null);
  }, [setSignupRequestId, setToolSessionId]);
  if (toolQuery.loading && !toolQuery.data) {
    return <Center py={8} width={'full'}>
        <Spinner />
      </Center>;
  }

  // GenericHero (and similar LP parents) set textAlign/align center on the section.
  // Chat must force start alignment so message rows, avatars, and composer stay LTR layout.
  return <VStack align={'stretch'} alignSelf={'stretch'} gap={4} textAlign={'start'} width={'full'} {...containerProps}>
      <SignupRequestChatIntakeAskBar
    // Guest homepage intake — document upload must work without a registered session
    bypassUploadAuth={true} disabled={isBootstrapping} hideFileAttach={hideFileAttach} placeholder={placeholder} placeholders={placeholders} onAsk={handleAsk} />

      {error && !isChatModalOpen && <Box color={'red.400'} fontSize={'sm'} textAlign={'start'} width={'full'}>
          {error}
        </Box>}

      {/* Chat + CTA modal: desktop container.xl panel; mobile full-screen takeover */}
      <Dialog.Root lazyMount motionPreset={'slide-in-bottom'} open={isChatModalOpen} placement={'center'} scrollBehavior={'outside'} onOpenChange={({
      open
    }) => {
      if (!open) {
        closeChatModal();
      }
    }}>
        <Portal>
          <Dialog.Backdrop backdropFilter={'blur(4px)'} bg={'blackAlpha.700'} />
          <Dialog.Positioner alignItems={{
          base: 'stretch',
          md: 'center'
        }} padding={{
          base: 0,
          md: 6
        }}>
            <Dialog.Content bgColor={'colorPalette.container'} borderColor={'colorPalette.border'} borderRadius={{
            base: 0,
            md: '2xl'
          }} borderWidth={{
            base: 0,
            md: '1px'
          }} boxShadow={{
            base: 'none',
            md: 'xl'
          }} display={'flex'} flexDirection={'column'} height={{
            base: '100dvh',
            md: 'min(85dvh, 900px)'
          }} marginY={{
            base: 0,
            md: 'auto'
          }} maxHeight={{
            base: '100dvh',
            md: 'min(85dvh, 900px)'
          }} maxWidth={{
            base: '100dvw',
            md: 'container.xl'
          }} minHeight={{
            base: '100dvh',
            md: 'unset'
          }} overflow={'hidden'} padding={0} textAlign={'start'} width={'full'}>
              <Dialog.Header alignItems={'center'} bg={'colorPalette.container'}
            // borderBottomWidth={'1px'}
            // borderColor={'colorPalette.border'}
            boxShadow={'sm'} flexShrink={0} gap={3} justifyContent={'space-between'} paddingEnd={3} paddingStart={{
              base: 4,
              md: 5
            }} paddingY={3} width={'full'} zIndex={1}>
                <StartupscomLogo />
                {/*<Dialog.Title asChild>*/}
                {/*  <Heading flex={1} fontWeight={'semibold'} lineClamp={1} minWidth={0} size={'md'}>*/}
                {/*    Get started*/}
                {/*  </Heading>*/}
                {/*</Dialog.Title>*/}
                <Dialog.CloseTrigger asChild insetEnd={'auto'} pos={'static'} top={'auto'}>
                  <CloseButton aria-label={'Close chat'} size={'md'} />
                </Dialog.CloseTrigger>
              </Dialog.Header>

              <Dialog.Body display={'flex'} flex={'1'} flexDirection={'column'} minHeight={0} overflow={'hidden'} padding={0}>
                <VStack align={'stretch'} flex={'1'} gap={0} minHeight={0} width={'full'}>
                  <Box bg={'bg.subtle'} display={'flex'} flex={'1'} flexDirection={'column'} height={'full'} minHeight={0} overflow={'hidden'} textAlign={'start'} width={'full'}>
                    {isBootstrapping && !threadId ? <Center height={'full'} minHeight={'40vh'} width={'full'}>
                        <VStack gap={3}>
                          <Spinner size={'lg'} />
                          <Text color={'fg.muted'} fontSize={'sm'}>
                            Starting your chat…
                          </Text>
                        </VStack>
                      </Center> : error && !threadId ? <Center height={'full'} minHeight={'40vh'} padding={6} width={'full'}>
                        <VStack gap={4} maxWidth={'sm'} textAlign={'center'}>
                          <Text color={'red.400'} fontSize={'sm'}>
                            {error}
                          </Text>
                          <Button size={'sm'} variant={'outline'} onClick={closeChatModal}>
                            Close
                          </Button>
                        </VStack>
                      </Center> : threadId ? <ChatThread bypassContentPermissions hideHeader threadId={threadId} /> : <Center height={'full'} minHeight={'40vh'} width={'full'}>
                        <Spinner size={'lg'} />
                      </Center>}
                  </Box>

                  {expanded && threadId && showSignupCta && <HStack align={'center'} bg={'colorPalette.card'} borderColor={'colorPalette.border'} borderTopWidth={'1px'} flexShrink={0} flexWrap={'wrap'} gap={3} justify={'center'} paddingX={{
                  base: 4,
                  md: 5
                }} paddingY={{
                  base: 4,
                  md: 5
                }} textAlign={'center'} width={'full'}>
                      <Heading size={{
                    base: 'sm',
                    md: 'md'
                  }}>Ready to accomplish this goal?</Heading>
                      <Button size={'md'} variant={'cta'} onClick={() => {
                    setPendingCommit(true);
                    setPrompt('register');
                  }}>
                        Get Started for Free <ChevronRightIcon />
                      </Button>
                    </HStack>}

                  {expanded && threadId && !showSignupCta && currentUser?.isRegistered && signupRequestId && !isCommitting && <HStack bg={'colorPalette.solid/50'} borderColor={'colorPalette.border'} borderTopWidth={'1px'} flexShrink={0} justify={'center'} paddingX={4} paddingY={4} width={'full'}>
                        <Button size={{
                    base: 'md',
                    md: 'xl'
                  }} variant={'cta'} onClick={() => {
                    commitAttemptedForUserRef.current = null;
                    setPendingCommit(true);
                  }}>
                          Continue to workspace <ChevronRightIcon />
                        </Button>
                      </HStack>}
                </VStack>
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Commit modal: analyze tool-chat → GroupRequest → manage workspace */}
      <Dialog.Root closeOnEscape={!isCommitting} closeOnInteractOutside={!isCommitting} motionPreset={'scale'} open={isCommitting || !!error && pendingCommit} placement={'center'} size={'md'} onOpenChange={({
      open: open_0
    }) => {
      // Block dismiss while the commit request is in flight
      if (!open_0 && !isCommitting) {
        setPendingCommit(false);
        setError(null);
        commitAttemptedForUserRef.current = null;
      }
    }}>
        <Portal>
          <Dialog.Backdrop backdropFilter={'blur(4px)'} bg={'blackAlpha.700'} />
          <Dialog.Positioner paddingX={4}>
            <Dialog.Content bgColor={'colorPalette.container'} borderColor={'colorPalette.border'} borderRadius={'2xl'} borderWidth={'1px'} boxShadow={'xl'} maxWidth={'28em'} padding={8} width={'full'}>
              <VStack align={'center'} gap={5} textAlign={'center'}>
                {isCommitting ? <>
                    <Spinner colorPalette={'orange'} size={'xl'} />
                    <Heading fontWeight={'semibold'} size={'md'}>
                      Analyzing your chat and setting up your workspace…
                    </Heading>
                    <Text color={'fg.muted'} fontSize={'sm'}>
                      We’re reading your conversation, filling in your goal, and creating your workspace. This
                      only takes a moment.
                    </Text>
                  </> : <>
                    <Heading color={'red.400'} fontWeight={'semibold'} size={'md'}>
                      We couldn’t finish setup
                    </Heading>
                    <Text color={'fg.muted'} fontSize={'sm'}>
                      {error}
                    </Text>
                    <HStack gap={3}>
                      <Button variant={'outline'} onClick={() => {
                    setPendingCommit(false);
                    setError(null);
                    commitAttemptedForUserRef.current = null;
                  }}>
                        Dismiss
                      </Button>
                      <Button variant={'cta'} onClick={() => {
                    commitAttemptedForUserRef.current = null;
                    setError(null);
                    setPendingCommit(true);
                    setCommitRetryKey(k => k + 1);
                  }}>
                        Try again
                      </Button>
                    </HStack>
                  </>}
              </VStack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </VStack>;
};
export default SignupRequestChatIntake;